import React from "react";
import { enqueueSnackbar } from "notistack";
import { legendItems } from "../constants/legendItems";

import "./../css/DateDialog.css";

interface DateDialogProps {
  isOpen: boolean;
  selectedDate: Date | null;
  selectedEvent?: any;
  onClose: () => void;
  onSave: () => void;
  selectedFilter?: string | null;
}

const DateDialog: React.FC<DateDialogProps> = ({
  isOpen,
  selectedDate,
  selectedEvent,
  onClose,
  onSave: onSaveCallback,
  selectedFilter,
}) => {
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const titlePresets = ["Früh", "Mittel", "Spät"];
  const [titleValue, setTitleValue] = React.useState<string>(selectedEvent?.title || "");
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  // Returns the color for the selected legend filter, or last used color, or default
  const getDefaultColor = React.useCallback(() => {
    if (!selectedEvent && selectedFilter) {
      const legend = legendItems.find(
        (item) =>
          item.label.toLowerCase() === selectedFilter?.toLowerCase() ||
          item.color === selectedFilter
      );
      if (legend) return legend.color;
    }
    return localStorage.getItem("lastUsedColor") || "#3788d8";
  }, [selectedEvent, selectedFilter]);

  const urlaubLegend = legendItems.find(item => item.label === "Urlaub");
  const urlaubColor = urlaubLegend ? urlaubLegend.color : "#d35400";
  const [currentBgColor, setCurrentBgColor] = React.useState<string>(selectedEvent?.bgColor || getDefaultColor());

  React.useEffect(() => {
    setCurrentBgColor(selectedEvent?.bgColor || getDefaultColor());
  }, [selectedEvent, selectedFilter, getDefaultColor]);

  React.useEffect(() => {
    setTitleValue(selectedEvent?.title || "");
  }, [selectedEvent, isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      // Setze Fokus auf das title Input-Feld nach kurzer Verzögerung
      setTimeout(() => {
        const titleInput = document.getElementById("title") as HTMLInputElement;
        if (titleInput) {
          titleInput.focus();
          titleInput.select();
        }
      }, 100);
    }
  }, [isOpen]);

  // Automatische Anpassung der Wiederholung bei Geburtstagsauswahl
  React.useEffect(() => {
    if (isOpen) {
      const colorSelect = document.getElementById("bgColor") as HTMLSelectElement;
      const wiederholenSelect = document.getElementById("wiederholen") as HTMLSelectElement;
      const ganzTaegigCheckbox = document.getElementById("ganzTaegig") as HTMLInputElement;

      const handleColorChange = () => {
        if (colorSelect?.value === "#f39c12") { // Geburtstag
          if (wiederholenSelect) {
            wiederholenSelect.value = "jaehrlich";
          }
          if (ganzTaegigCheckbox) {
            ganzTaegigCheckbox.checked = true;
          }
        }
      };

      colorSelect?.addEventListener("change", handleColorChange);

      return () => {
        colorSelect?.removeEventListener("change", handleColorChange);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedEvent) return;
    const value = e.target.value;
    const presets: { [key: string]: { start: string; end: string } } = {
      "Früh":  { start: "07:00", end: "13:00" },
      "Mittel": { start: "09:00", end: "14:00" },
      "Spät":  { start: "15:00", end: "22:00" },
    };
    const preset = presets[value];
    if (preset) {
      const startTimeInput = document.getElementById("startTime") as HTMLInputElement;
      const endTimeInput = document.getElementById("endTime") as HTMLInputElement;
      if (startTimeInput) startTimeInput.value = preset.start;
      if (endTimeInput) endTimeInput.value = preset.end;
    }
  };

  const getDefaultStartTime = () => {
    if (!selectedDate) return "";
    const now = new Date();
    const dateWithCurrentTime = new Date(selectedDate);
    dateWithCurrentTime.setHours(now.getHours());
    dateWithCurrentTime.setMinutes(now.getMinutes());
    // Format as HH:mm for <input type="time">
    const hh = String(dateWithCurrentTime.getHours()).padStart(2, '0');
    const mm = String(dateWithCurrentTime.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const getDefaultEndTime = () => {
    if (!selectedDate) return "";
    const now = new Date();
    const dateWithCurrentTime = new Date(selectedDate);
    dateWithCurrentTime.setHours(now.getHours() + 2);
    dateWithCurrentTime.setMinutes(now.getMinutes());
    // Format as HH:mm for <input type="time">
    const hh = String(dateWithCurrentTime.getHours()).padStart(2, '0');
    const mm = String(dateWithCurrentTime.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };


  const onSave = (event: React.MouseEvent<HTMLButtonElement>) => {
    const titleInput = document.getElementById("title") as HTMLInputElement;
    const startTimeInput = document.getElementById("startTime") as HTMLInputElement;
    const endTimeInput = document.getElementById("endTime") as HTMLInputElement;
    const colorInput = document.getElementById("bgColor") as HTMLInputElement;
    const wiederholenInput = document.getElementById("wiederholen") as HTMLInputElement;
    const ganzTaegigInput = document.getElementById("ganzTaegig") as HTMLInputElement;
    const erinnernInput = document.getElementById("erinnern") as HTMLInputElement;
    const endDateInput = document.getElementById("endDate") as HTMLInputElement;

    const title = titleInput?.value || "";
    let startTimeValue = startTimeInput?.value;
    let endTimeValue = endTimeInput?.value;
    if ((!startTimeValue || startTimeValue === "") && selectedEvent?.startTime) {
      const t = selectedEvent.startTime.split('T')[1];
      startTimeValue = t ? t.slice(0,5) : "";
    }
    if ((!endTimeValue || endTimeValue === "") && selectedEvent?.endTime) {
      const t = selectedEvent.endTime.split('T')[1];
      endTimeValue = t ? t.slice(0,5) : "";
    }
    const dateInput = document.getElementById("date") as HTMLInputElement;
    let selectedDateValue: Date | null = null;
    if (dateInput && dateInput.value) {
      selectedDateValue = new Date(dateInput.value + 'T00:00');
    } else if (selectedDate) {
      selectedDateValue = selectedDate;
    } else if (selectedEvent?.startTime) {
      const [datePart] = selectedEvent.startTime.split('T');
      selectedDateValue = new Date(datePart);
    }
    const bgColor = colorInput?.value || "";
    // Enddatum für Urlaub
    let selectedEndDateValue: Date | null = null;
    if (endDateInput && endDateInput.value) {
      selectedEndDateValue = new Date(endDateInput.value + 'T00:00');
    } else if (selectedEvent?.endTime && isUrlaubSelected()) {
      const [datePart] = selectedEvent.endTime.split('T');
      selectedEndDateValue = new Date(datePart);
    }
    const wiederholen = wiederholenInput?.value || "nie";
    const ganzTaegig = ganzTaegigInput?.checked ? 1 : 0;
    const erinnern = erinnernInput?.checked ? 1 : 0;

    function formatLocalDateTime(dateObj: Date) {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    // Hilfsfunktion: Prüft, ob Urlaub ausgewählt ist
    function isUrlaubSelected() {
      const urlaubLegend = legendItems.find(item => item.label === "Urlaub");
      return bgColor === (urlaubLegend ? urlaubLegend.color : "#d35400");
    }

    let startTime = "";
    let endTime = "";
    // Startzeit
    if (startTimeValue) {
      let baseDate = selectedDateValue;
      if (!baseDate && selectedEvent?.startTime) {
        const [datePart] = selectedEvent.startTime.split('T');
        baseDate = new Date(datePart);
      }
      if (baseDate) {
        const [h, m] = startTimeValue.split(":");
        const d = new Date(baseDate);
        d.setHours(Number(h), Number(m), 0, 0);
        startTime = formatLocalDateTime(d);
      }
    } else if (selectedEvent?.startTime) {
      startTime = selectedEvent.startTime;
    }
    // Endzeit
    if (isUrlaubSelected() && selectedEndDateValue) {
      // Für Urlaub: Enddatum + Endzeit kombinieren
      let baseDate = selectedEndDateValue;
      if (endTimeValue) {
        const [h, m] = endTimeValue.split(":");
        const d = new Date(baseDate);
        d.setHours(Number(h), Number(m), 0, 0);
        endTime = formatLocalDateTime(d);
      } else {
        // Falls keine Endzeit: 23:59
        const d = new Date(baseDate);
        d.setHours(23, 59, 0, 0);
        endTime = formatLocalDateTime(d);
      }
    } else if (endTimeValue) {
      let baseDate = selectedDateValue;
      if (!baseDate && selectedEvent?.endTime) {
        const [datePart] = selectedEvent.endTime.split('T');
        baseDate = new Date(datePart);
      }
      if (baseDate) {
        const [h, m] = endTimeValue.split(":");
        const d = new Date(baseDate);
        d.setHours(Number(h), Number(m), 0, 0);
        endTime = formatLocalDateTime(d);
      }
    } else if (selectedEvent?.endTime) {
      endTime = selectedEvent.endTime;
    }

    // Validierung
    const newErrors: { [key: string]: string } = {};
    if (!title.trim()) {
      newErrors.title = "Bitte geben Sie einen Titel ein.";
    }
    if (!startTime) {
      if (!(selectedEvent && selectedEvent.startTime)) {
        newErrors.startTime = "Bitte geben Sie eine Startzeit ein.";
      }
    }
    if (!endTime) {
      if (!(selectedEvent && selectedEvent.endTime)) {
        newErrors.endTime = "Bitte geben Sie eine Endzeit ein.";
      }
    }
    if (!bgColor) {
      newErrors.bgColor = "Bitte wählen Sie eine Farbe aus.";
    }
    // Zeitraum-Validierung für Urlaub
    if (isUrlaubSelected()) {
      if (!selectedEndDateValue) {
        newErrors.endTime = "Bitte geben Sie ein Enddatum für den Urlaub an.";
      } else if (selectedDateValue && selectedEndDateValue < selectedDateValue) {
        newErrors.endTime = "Das Enddatum darf nicht vor dem Startdatum liegen.";
      }
    }
    // Zeitvergleich lokal, nicht über Date-Objekte (wegen fehlender Zeitzone)
    if (endTime && startTime) {
      const [startDatum, startUhrzeit] = startTime.split('T');
      const [endDatum, endUhrzeit] = endTime.split('T');
      const startVal = startDatum + startUhrzeit;
      const endVal = endDatum + endUhrzeit;
      if (endVal < startVal) {
        newErrors.endTime = "Die Endzeit darf nicht vor der Startzeit liegen.";
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const isUpdate = selectedEvent && selectedEvent.id;
    const apiUrl = isUpdate
      ? "https://norbertspringer.de/calendar/api/updateTermin.php"
      : "https://norbertspringer.de/calendar/api/setTermin.php";

    const eventData = isUpdate
      ? {
          id: selectedEvent.id,
          title,
          startTime,
          endTime,
          bgColor,
          wiederholen,
          ganzTaegig,
          erinnern,
        }
      : {
          title,
          startTime,
          endTime,
          bgColor,
          wiederholen,
          ganzTaegig,
          erinnern,
        };


    // Speichere gewählte Farbe für nächsten Eintrag
    localStorage.setItem("lastUsedColor", bgColor);
    localStorage.setItem("lastUsedTitle", title);

    fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(eventData),
    })
      .then((response) => response.json())
      .then((data) => {
        //console.log("Success:", data);
        enqueueSnackbar(data.message || "Termin erfolgreich gespeichert", {
          variant: "success",
        });
        onSaveCallback();
        onClose();
      })
      .catch((error) => {
        console.error("Error:", error);
        enqueueSnackbar("Fehler beim Speichern des Termins", {
          variant: "error",
        });
        onClose();
      });
  };

  const onDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!selectedEvent || !selectedEvent.id) {
      onClose();
      return;
    }

    if (!window.confirm("Möchten Sie diesen Termin wirklich löschen?")) {
      return;
    }

    fetch("https://norbertspringer.de/calendar/api/deleteTermin.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id: selectedEvent.id }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Deleted:", data);
        enqueueSnackbar(data.message || "Termin erfolgreich gelöscht", {
          variant: "success",
        });
        onSaveCallback();
        onClose();
      })
      .catch((error) => {
        console.error("Error deleting:", error);
        enqueueSnackbar("Fehler beim Löschen des Termins", {
          variant: "error",
        });
        onClose();
      });
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <h2 className="dialog-header">
          {selectedEvent ? "Termin bearbeiten:" : "Neuer Termin:"}
        </h2>
        <div className="dialog-form">
          <div className="form-full-width">
            <label htmlFor="title">Titel:</label>
            <div className="error-wrapper">
              <div className="title-autocomplete">
                <input
                  type="text"
                  className="dialog-input"
                  name="title"
                  id="title"
                  placeholder="Titel"
                  value={titleValue}
                  onChange={(e) => {
                    setTitleValue(e.target.value);
                    handleTitleChange(e);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  autoComplete="off"
                />
                {showSuggestions && (
                  <ul className="title-suggestions-list">
                    {titlePresets
                      .filter((s) =>
                        titleValue === "" ||
                        s.toLowerCase().startsWith(titleValue.toLowerCase())
                      )
                      .map((suggestion) => (
                        <li
                          key={suggestion}
                          onPointerDown={() => {
                            setTitleValue(suggestion);
                            handleTitleChange({
                              target: { value: suggestion },
                            } as React.ChangeEvent<HTMLInputElement>);
                            setShowSuggestions(false);
                          }}
                        >
                          {suggestion}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
              {errors.title && (
                <span className="dialog-error">{errors.title}</span>
              )}
            </div>
          </div>
{/* input date */}
        <div className="form-full-width">
            <label htmlFor="date">Datum:</label>
            <div className="error-wrapper">
              <input
                className="dialog-input"
                type="date"
                name="date"
                id="date"
                defaultValue={
                  selectedEvent?.startTime
                    ? (() => {
                        const d = selectedEvent.startTime.split('T')[0];
                        return d || '';
                      })()
                    : selectedDate
                    ? selectedDate.toISOString().slice(0, 10)
                    : ''
                }
              />
              {errors.startTime && (
                <span className="dialog-error">{errors.startTime}</span>
              )}
            </div>
        </div>
        {/* Enddatum nur für Urlaub */}
        {currentBgColor === urlaubColor && (
          <div className="form-full-width">
            <label htmlFor="endDate">Enddatum:</label>
            <div className="error-wrapper">
              <input
                className="dialog-input"
                type="date"
                name="endDate"
                id="endDate"
                defaultValue={
                  selectedEvent?.endTime
                    ? (() => {
                        const d = selectedEvent.endTime.split('T')[0];
                        return d || '';
                      })()
                    : selectedDate
                    ? selectedDate.toISOString().slice(0, 10)
                    : ''
                }
              />
              {errors.endTime && (
                <span className="dialog-error">{errors.endTime}</span>
              )}
            </div>
          </div>
        )}
          <div className="form-full-width">
            <label htmlFor="startTime">Beginn:</label>
            <div className="error-wrapper">
              <input
                className="dialog-input"
                type="time"
                name="startTime"
                id="startTime"
                defaultValue={
                  selectedEvent?.startTime
                    ? (() => {
                        // If editing, extract HH:mm from ISO string
                        const t = selectedEvent.startTime.split('T')[1];
                        return t ? t.slice(0,5) : '';
                      })()
                    : getDefaultStartTime()
                }
              />
              {errors.startTime && (
                <span className="dialog-error">{errors.startTime}</span>
              )}
            </div>
          </div>

          <div className="form-full-width">
            <label htmlFor="endTime">Ende:</label>
            <div className="error-wrapper">
              <input
                className="dialog-input"
                type="time"
                name="endTime"
                id="endTime"
                defaultValue={
                  selectedEvent?.endTime
                    ? (() => {
                        const t = selectedEvent.endTime.split('T')[1];
                        return t ? t.slice(0,5) : '';
                      })()
                    : getDefaultEndTime()
                }
              />
              {errors.endTime && (
                <span className="dialog-error">{errors.endTime}</span>
              )}
            </div>
          </div>

          <div className="form-full-width">
            <label htmlFor="bgColor">Kalender:</label>
            <div className="error-wrapper">
              <select
                className="dialog-input"
                name="bgColor"
                id="bgColor"
                value={currentBgColor}
                onChange={e => setCurrentBgColor(e.target.value)}
              >
                {legendItems.map((item) => (
                  <option key={item.color} value={item.color}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.bgColor && (
                <span className="dialog-error">{errors.bgColor}</span>
              )}
            </div>
          </div>

          <div className="form-full-width">
            <label htmlFor="wiederholen">Wiederholen:</label>
            <div className="error-wrapper">
              <select
                className="dialog-input"
                name="wiederholen"
                id="wiederholen"
                defaultValue={selectedEvent?.wiederholen || "nie"}
              >
                <option value="nie">Nie</option>
                <option value="taeglich">Täglich</option>
                <option value="woechentlich">Wöchentlich</option>
                <option value="monatlich">Monatlich</option>
                <option value="jaehrlich">Jährlich</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="erinnern">Erinnern:</label>
              <input
                className="dialog-checkbox"
                type="checkbox"
                name="erinnern"
                id="erinnern"
                defaultChecked={
                  selectedEvent?.erinnern === 1 ||
                  selectedEvent?.erinnern === "1"
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="ganzTaegig">Ganztägig:</label>
              <input
                className="dialog-checkbox"
                type="checkbox"
                name="ganzTaegig"
                id="ganzTaegig"
                defaultChecked={
                  selectedEvent?.ganzTaegig === 1 ||
                  selectedEvent?.ganzTaegig === "1"
                }
              />
            </div>
          </div>
        </div>
        <div className="dialog-actions">
          <button onClick={onDelete} className="dialog-button-delete">
            Löschen
          </button>
          <button onClick={onClose} className="dialog-button-cancel">
            Abbrechen
          </button>

          <button onClick={onSave} className="dialog-button">
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateDialog;
